import React, { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import classNames from "classnames";
import { connect } from "react-redux";

import { compose, graphql } from "lib/apolloClient";
import { ChevronUpOutlined } from "@toddle-edu/ds-icons";

import { useHandleScroll } from "Utils/CustomHooks";
import { prepareLoadMoreFeed, onEnterOrSpaceKeyDown } from "Utils";
import AnimateHeight from "UIComponents/AnimateHeight";
import I18nHOC from "UIComponents/I18nHOC";
import withPusherBind from "UIComponents/PusherConsumerHoc";

import classes from "./IntraOrgWorkflowProgressMenu.scss";
import WorkflowItem from "./components/WorkflowItem";
import { getWorkflowItemTitle } from "./modules/Utils";
import { getJobFeedQuery } from "Integrations/modules/IntegrationsQueries";
import ManageWorkflowsModal from "AppComponents/ManageWorkflowsModal/ManageWorkflowsModal";
import {
  ManageWorkflowModalProvider,
  useManageWorkflowModal,
} from "AppComponents/ManageWorkflowsModal/modules/ManageWorkflowModalContext";

const formatJobData = ({ jobs, t }) =>
  jobs.map(({ node }) => {
    const { id, createdAt } = node;
    const subtask = _.get(node, "workflowProgress.subTaskData", {});
    const {
      workflowTaskOrgLevelLocaleKey,
      workflowTaskOrgLevelLocaleParams,
    } = _.get(subtask, "parsedData", {});

    return {
      id,
      createdAt,
      title: getWorkflowItemTitle({
        t,
        locale: workflowTaskOrgLevelLocaleKey,
        localeParams: workflowTaskOrgLevelLocaleParams,
      }),
      subTaskId: _.get(subtask, "id", null),
    };
  });

const getJobsWithProgressToast = jobs =>
  _.filter(jobs, ({ node }) => {
    return _.get(node, "metadata.showProgressToast", false);
  });

const WorkflowItemContainer = React.memo(
  ({ jobs, expandedJobId, updateExpandedJobId, t, jobFeedVariables }) =>
    formatJobData({ jobs, t }).map(job => (
      <WorkflowItem
        item={job}
        key={job.id}
        expandedJobId={expandedJobId}
        updateExpandedJobId={updateExpandedJobId}
        jobFeedVariables={jobFeedVariables}
      />
    ))
);

WorkflowItemContainer.displayName = "WorkflowItemContainer";

const WorkflowProgressOrgMenuInner = ({
  t,
  jobs,
  loadMoreFeed,
  hasNextPage,
  networkStatus,
  jobFeedVariables,
}) => {
  const [isMenuExpanded, setIsMenuExpanded] = useState(true);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const scrollContainerRef = useRef(null);
  const { setIsModalOpen } = useManageWorkflowModal();

  // Filter jobs to show only those with showProgressToast: true
  const filteredJobs = getJobsWithProgressToast(jobs);

  const toggleIsMenuExpanded = useCallback(() => {
    setExpandedJobId(null);
    setIsMenuExpanded(prevState => !prevState);
  }, []);

  const updateExpandedJobId = useCallback(
    id => setExpandedJobId(prevId => (prevId === id ? null : id)),
    []
  );

  const onModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef: useCallback(() => scrollContainerRef.current, []),
    hookDependencies: [scrollContainerRef.current],
  });

  useEffect(() => {
    setExpandedJobId(filteredJobs?.[0]?.node?.id || null);
  }, [filteredJobs]);

  return (
    <>
      {filteredJobs.length ? (
        <div className={classes.container}>
          <div
            className={classes.headerContainer}
            onClick={toggleIsMenuExpanded}
            onKeyDown={e => onEnterOrSpaceKeyDown(e, toggleIsMenuExpanded)}
            role="button"
            tabIndex={0}
            aria-expanded={isMenuExpanded}
            aria-label={t("workflows:sync_in_progress_with_count", {
              count: filteredJobs.length,
            })}
          >
            <div className={classes.headerText}>
              {t("workflows:sync_in_progress_with_count", {
                count: filteredJobs.length,
              })}
            </div>
            <ChevronUpOutlined
              variant="subtle"
              size="xxx-small"
              className={classNames(classes.arrowContainer, {
                [classes.openArrow]: isMenuExpanded,
                [classes.closeArrow]: !isMenuExpanded,
              })}
            />
          </div>
          <AnimateHeight duration={300} height={!isMenuExpanded ? 0 : "auto"}>
            <div className={classes.scrollContainer} ref={scrollContainerRef}>
              <WorkflowItemContainer
                t={t}
                jobs={filteredJobs}
                expandedJobId={expandedJobId}
                updateExpandedJobId={updateExpandedJobId}
                jobFeedVariables={jobFeedVariables}
              />
            </div>
          </AnimateHeight>
        </div>
      ) : null}

      <ManageWorkflowsModal
        onClose={onModalClose}
        jobFeedVariables={jobFeedVariables}
      />
    </>
  );
};

export const getFilterVariablesMemoized = _.memoize(
  ({ organizationId }) => ({
    fetchWorkflowProgress: true,
    id: organizationId,
    filters: { jobType: ["WORKFLOW_JOB"], status: "PROCESSING" },
    first: 25,
    portalType: "PLANNER",
  }),
  ({ organizationId }) => String(organizationId)
);

const mapStateToProps = state => {
  const organizationId = state.login.userInfo.org_id;
  return {
    jobFeedVariables: getFilterVariablesMemoized({ organizationId }),
    orgIds: [organizationId],
  };
};

const ConnectedWorkflowProgressOrgMenu = compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, null),
  graphql(getJobFeedQuery, {
    name: "getJobFeed",
    options: ({ jobFeedVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: jobFeedVariables,
    }),
    props: ({ getJobFeed, ownProps: { jobFeedVariables } }) => {
      const jobs = _.get(getJobFeed, "node", {});
      const pageInfo = _.get(getJobFeed, "node.jobFeed.pageInfo", {});

      return {
        getJobFeed,
        jobs: _.get(jobs, "jobFeed.edges", []),
        onPusherEventTriggered: getJobFeed.refetch,
        isData: !_.isEmpty(jobs),
        isLoading: [1, 2].includes(getJobFeed.networkStatus),
        isMoreJobsLoading: getJobFeed.networkStatus === 3,
        hasNextPage: pageInfo.hasNextPage || false,
        networkStatus: getJobFeed.networkStatus,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            ...jobFeedVariables,
            afterV2: pageInfo.endCursor || null,
          },
          queryData: getJobFeed,
          key: "jobFeed",
        }),
      };
    },
  }),
  withPusherBind([
    "workflowProgressStarted",
    "workflowJobCompleted",
    "workflowProgressUpdate",
  ])
)(WorkflowProgressOrgMenuInner);

const IntraOrgWorkflowProgressMenu = props => {
  return (
    <ManageWorkflowModalProvider>
      <ConnectedWorkflowProgressOrgMenu {...props} />
    </ManageWorkflowModalProvider>
  );
};

export default IntraOrgWorkflowProgressMenu;
